import _ from 'lodash';
import moment from 'moment';
import { createContext } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { DATE_FORMAT, safeFormatDateString } from './dateUtils';
import { isNilOrEmpty } from './ramdaUtils';
import { TRANSLATE_DATA_COLUMN_VALUES_SEPARATOR } from '../constants/SavedSearches';
import { safeToFixedDecimalScale } from '../form-designer/utils/controls/ssNumericTextBox.utils';
import { getDecimalScale } from '../form-designer/utils/form-manager.utils';

export const isTrue = (value) => value === true || value === 'true';
export const isFalse = (value) => value === false || value === 'false';

const OPEN_PARENTHESIS_FILTER = {
  openGroup: true,
  fieldName: '',
  sqlOperator: '',
  value: '',
  relationalOperator: '',
  closeGroup: false,
};

const CLOSE_PARENTHESIS_FILTER = {
  openGroup: false,
  fieldName: '',
  sqlOperator: '',
  value: '',
  relationalOperator: '',
  closeGroup: true,
};

const dataTypeSqlOperators = {
  'System.String': 'CT',
  'System.Int32': 'EQ',
  'System.Byte': 'EQ',
  'System.Boolean': 'EQ',
  'System.DateTime': 'EQ',
  'System.Decimal': 'EQ',
};

const valueNotEmpty = ({ value }) => !isNilOrEmpty(value);

export const sanitizeFilterGroups = (filters) => {
  const groups = [];

  for (let i = 0; i < filters.length; i++) {
    const currentFilter = filters[i];
    const addNonEmptyFilter = currentFilter.fieldName !== ''; // This tells us if the filter is empty, for example just a group opener/closer with no condition

    if (isTrue(currentFilter.openGroup)) {
      const closesGroup = isTrue(currentFilter.closeGroup);
      const closingIndex = closesGroup ? i : undefined;
      const nonEmptyFilters = closesGroup && addNonEmptyFilter ? 1 : 0;

      groups.unshift({ openingIndex: i, nonEmptyFilters, closingIndex });
    } else if (isTrue(currentFilter.closeGroup)) {
      const latestUnclosedGroup = groups.find((group) => _.isNil(group.closingIndex));

      if (addNonEmptyFilter) {
        latestUnclosedGroup.nonEmptyFilters++;
      }
      latestUnclosedGroup.closingIndex = i;
    }

    if (addNonEmptyFilter) {
      groups.forEach((group) => _.isNil(group.closingIndex) && group.nonEmptyFilters++);
    }
  }

  const indexesToRemove = groups.reduce((acc, group) => {
    if (group.nonEmptyFilters === 0) {
      return [...acc, group.openingIndex, group.closingIndex];
    }
    return acc;
  }, []);

  return indexesToRemove.length > 0
    ? filters.filter((f, i) => {
        return !indexesToRemove.includes(i);
      })
    : filters;
};

export const getInitialApolloTableState = (
  initialRowsPerPage,
  rowsPerPageOptions,
  initialSortedColumn,
  initialSortOrder,
  initialTableFilters,
  initialPage
) => ({
  currentPage: initialPage ?? 0,
  currentPageSize: initialRowsPerPage || rowsPerPageOptions[0],
  currentSortedColumn: initialSortedColumn,
  currentSortOrder: initialSortOrder,
  filters: initialTableFilters ?? [],
});

export const removeCustomFiltersWithEmptyValue = (customFilter) => {
  if (!Array.isArray(customFilter)) {
    return [];
  }

  return sanitizeFilterGroups(
    customFilter
      .map((filter) => {
        if (!isNilOrEmpty(filter.value)) {
          return filter;
        }
        if (['ISNULL', 'ISNOTNULL'].includes(filter.sqlOperator)) {
          return filter;
        }

        if (isTrue(filter.openGroup) && isFalse(filter.closeGroup)) {
          return { ...OPEN_PARENTHESIS_FILTER };
        }
        if (isTrue(filter.closeGroup) && isFalse(filter.openGroup)) {
          return { ...CLOSE_PARENTHESIS_FILTER };
        }

        return null;
      })
      .filter((f) => !_.isNil(f))
  );
};

export const getLoadParameters = ({
  customFilter,
  entityName,
  searchName,
  dataKey,
  currentPage,
  currentPageSize,
  referencedColumns,
  dynamicParamCollection,
  advancedSearchCriteria,
  stateFilters,
  currentSortedColumn,
  currentSortOrder,
  metadata,
}) => {
  const filteredCustomFilter = removeCustomFiltersWithEmptyValue(customFilter);
  const groupedCustomFilter =
    filteredCustomFilter.length > 0
      ? [OPEN_PARENTHESIS_FILTER, ...filteredCustomFilter, CLOSE_PARENTHESIS_FILTER]
      : [];

  const loadDataParameters = {
    entityName,
    searchName,
    dataKey,
    currentPageIndex: currentPage,
    pageSize: currentPageSize,
    referencedColumns,
    dynamicParamCollection,
    advancedsearchcriteria: advancedSearchCriteria,
    filter: groupedCustomFilter.concat(stateFilters.filter(valueNotEmpty)),
    sortExpression:
      currentSortedColumn && `${currentSortedColumn} ${currentSortOrder.toUpperCase()}`,
    metadata,
  };

  return loadDataParameters;
};

const getColumnSqlOperator = (columnName, columnsDataTypeMap) =>
  columnsDataTypeMap[columnName]?.translateData
    ? 'EQ'
    : dataTypeSqlOperators[columnsDataTypeMap[columnName]?.dataType] || 'CT';

export const resolveFilters = (filters, columns = []) => {
  const columnsDataTypeMap = columns.reduce(
    (acc, column) => ({ ...acc, [column.accessor]: column }),
    {}
  );

  return _.isPlainObject(filters)
    ? _.flattenDeep(
        Object.keys(filters).map((columnName) => {
          if (filters[columnName]?.includes(TRANSLATE_DATA_COLUMN_VALUES_SEPARATOR)) {
            const selectedValues = filters[columnName].split(
              TRANSLATE_DATA_COLUMN_VALUES_SEPARATOR
            );

            return selectedValues.map((value, i) => ({
              openGroup: i === 0,
              fieldName: columnName,
              sqlOperator: getColumnSqlOperator(columnName, columnsDataTypeMap),
              value,
              relationalOperator: i === selectedValues.length - 1 ? 'AND' : 'OR',
              closeGroup: i === selectedValues.length - 1,
            }));
          }

          if (isNilOrEmpty(filters[columnName])) {
            return {
              openGroup: false,
              fieldName: columnName,
              sqlOperator: getColumnSqlOperator(columnName, columnsDataTypeMap),
              value: filters[columnName],
              relationalOperator: 'AND',
              closeGroup: false,
            };
          }

          switch (columnsDataTypeMap[columnName]?.dataType) {
            case 'System.DateTime':
              return [
                {
                  openGroup: true,
                  fieldName: columnName,
                  sqlOperator: 'GTEQ',
                  value: moment
                    .utc(filters[columnName], DATE_FORMAT)
                    .toISOString()
                    ?.split('T')[0],
                  relationalOperator: 'AND',
                  closeGroup: false,
                },
                {
                  openGroup: false,
                  fieldName: columnName,
                  sqlOperator: 'LT',
                  value: moment
                    .utc(filters[columnName], DATE_FORMAT)
                    .add(1, 'day')
                    .toISOString()
                    ?.split('T')[0],
                  relationalOperator: 'AND',
                  closeGroup: true,
                },
              ];
            case 'System.Byte':
            case 'System.Boolean': {
              const filterValueToLower =
                typeof filters[columnName] === 'string'
                  ? filters[columnName].toLowerCase().trim()
                  : filters[columnName];
              const filterValue = ['yes', 'true', '1', 1, true].includes(filterValueToLower)
                ? 1
                : ['no', 'false', '0', 0, false].includes(filterValueToLower)
                ? 0
                : filters[columnName];

              return {
                openGroup: false,
                fieldName: columnName,
                sqlOperator: getColumnSqlOperator(columnName, columnsDataTypeMap),
                value: filterValue,
                relationalOperator: 'AND',
                closeGroup: false,
              };
            }
            default:
              return {
                openGroup: false,
                fieldName: columnName,
                sqlOperator: getColumnSqlOperator(columnName, columnsDataTypeMap),
                value: filters[columnName],
                relationalOperator: 'AND',
                closeGroup: false,
              };
          }
        })
      ).filter(valueNotEmpty)
    : [];
};

export const getContentDispositionFile = (contentDisposition) => {
  const fileNameWithExtension = contentDisposition?.split('filename=')[1];

  if (!fileNameWithExtension) {
    return;
  }

  const parts = fileNameWithExtension.split('.');
  parts.pop();

  return {
    name: parts.join('.'),
    extension: _.last(fileNameWithExtension?.split('.')),
  };
};

export const getTranslatedColumnListKey = (savedSearchName, columnName) =>
  `${savedSearchName}|${columnName}`;

export const SavedSearchNameContext = createContext(null);

export const treeViewDataToOptions = (treeViewData) => {
  if (_.isNil(treeViewData)) {
    return [];
  }

  const id = treeViewData.Value || uuidv4();
  const value = treeViewData.IsSelectable ? treeViewData.Value : id;

  const newOption = {
    id,
    value,
    dataType: treeViewData.ColumnType,
    text: treeViewData.Text,
    group: !treeViewData.IsSelectable,
  };

  return [newOption].concat(
    treeViewData.Items
      ? _.flattenDeep(treeViewData.Items.map((item) => treeViewDataToOptions(item)))
      : []
  );
};

export const preprocessTreeViewData = (tree) => {
  return tree.map((node) => {
    const newNode = {
      isSelectable: node.IsSelectable,
      label: node.Text,
      value: node.Value,
      id: node.Value || uuidv4(),
      childrenNodes: node.Items || [],
    };

    if (newNode.childrenNodes.length) {
      newNode.childrenNodes = preprocessTreeViewData(newNode.childrenNodes);
    }

    return newNode;
  });
};

export function safeFormatDecimalValue(value, formatType) {
  const decimalScale = getDecimalScale('System.Decimal', formatType, value);
  return safeToFixedDecimalScale(decimalScale, value);
}

export const dataTypeFormatters = {
  'System.DateTime': safeFormatDateString,
  'System.Decimal': safeFormatDecimalValue,
};
